import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, FloatButton, Input, Modal, QRCode, Select, Space, Tooltip, Typography } from 'antd';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { PERMISSIONS, useUser } from '../../context/UserContext';
import { Branding } from '../../service/branding-service';
import './whatsapp-support.css';

const WHATSAPP_GREEN = '#25D366';

// Açıklama QR'a URL-encoded olarak girer (Türkçe harf = 6 karakter). 400 sınırı, en kötü durumda
// bile metnin 'L' düzeyindeki QR kapasitesine (~2900 karakter) sığmasını garanti eder.
const DESCRIPTION_MAX = 400;

/**
 * WhatsApp destek butonu. Footer ekranda tamamen görünüyorsa footer'ın en sağına (kuantumbulut.com
 * yanındaki yuvaya) yerleşir; görünmüyorsa sağ altta yüzer — her durumda ekranda tek buton vardır.
 *
 * Sunucuya hiçbir şey gitmez: kategori + açıklama + domain/kullanıcı bilgisiyle bir wa.me linki
 * üretilir. Masaüstünde aynı link QR olarak da gösterilir; telefonla okutunca WhatsApp mesaj
 * doldurulmuş halde açılır. Numara ve kategoriler Marka ekranından (DB) gelir; numara yoksa buton çıkmaz.
 */
export default function WhatsAppSupport({ app = 'RIS' }) {
    const { t } = useTranslation();
    const { user, hasPermission } = useUser();
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState();
    const [description, setDescription] = useState('');
    const [slot, setSlot] = useState(null);
    const [docked, setDocked] = useState(false);

    // Footer tamamen görünürken buton footer'a yerleşir (RIS'te footer sabit olduğu için pratikte hep orada).
    useEffect(() => {
        const footer = document.querySelector('.app-footer');
        setSlot(document.getElementById('footer-support-slot'));
        if (!footer || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            ([entry]) => setDocked(entry.intersectionRatio >= 0.99),
            { threshold: [0, 0.99, 1] }
        );
        observer.observe(footer);
        return () => observer.disconnect();
    }, []);

    const phone = Branding.supportWhatsApp();
    const categories = Branding.supportCategories();

    const message = [
        `*[${app}] ${t('Support Request')}*`,
        category && `${t('Category')}: ${category}`,
        `Domain: ${window.location.host}`,
        `${t('User')}: ${user?.data?.username || '-'}${user?.data?.name ? ` (${user.data.name})` : ''}`,
        `${t('Page')}: ${window.location.pathname}`,
        `${t('Date')}: ${moment().format('DD.MM.YYYY HH:mm')}`,
        '',
        `${t('Description')}:`,
        description.trim() || '-',
    ].filter(line => line !== false && line !== undefined).join('\n');

    if (!phone || !hasPermission(PERMISSIONS.WHATSAPP_SUPPORT)) return null;

    const ready = description.trim().length > 0 && (categories.length === 0 || !!category);
    const link = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    const close = () => {
        setOpen(false);
        setDescription('');
        setCategory(undefined);
    };

    return (
        <>
            {docked && slot
                ? createPortal(
                    <Tooltip title={t('WhatsApp Support')}>
                        <button
                            type="button"
                            className="whatsapp-footer-btn"
                            aria-label={t('WhatsApp Support')}
                            onClick={() => setOpen(true)}
                        >
                            <FaWhatsapp />
                        </button>
                    </Tooltip>,
                    slot
                )
                : (
                    <FloatButton
                        className="whatsapp-float"
                        icon={<FaWhatsapp />}
                        tooltip={t('WhatsApp Support')}
                        onClick={() => setOpen(true)}
                        style={{
                            width: 52,
                            height: 52,
                            insetInlineEnd: 24,
                            bottom: 24,
                        }}
                    />
                )}
            <Modal
                open={open}
                title={<Space><FaWhatsapp style={{ color: WHATSAPP_GREEN }} />{t('WhatsApp Support')}</Space>}
                onCancel={close}
                footer={null}
                destroyOnClose
                width={isMobile ? undefined : 640}
            >
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <Space direction="vertical" style={{ flex: '1 1 280px', minWidth: 0 }}>
                        {/* Marka ekranında kategori tanımlanmadıysa seçim alanı hiç çıkmaz. */}
                        {categories.length > 0 && (
                            <>
                                <Typography.Text strong>{t('Category')}</Typography.Text>
                                <Select
                                    value={category}
                                    onChange={setCategory}
                                    placeholder={t('Select')}
                                    style={{ width: '100%' }}
                                    options={categories.map(c => ({ value: c, label: c }))}
                                />
                            </>
                        )}
                        <Typography.Text strong>{t('Description')}</Typography.Text>
                        <Input.TextArea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            maxLength={DESCRIPTION_MAX}
                            showCount
                            autoSize={{ minRows: 5, maxRows: 10 }}
                            placeholder={t('Briefly describe the issue')}
                            autoFocus
                        />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {t('Domain, user name and current page are added to the message automatically.')}
                        </Typography.Text>
                        <Button
                            type="primary"
                            block
                            icon={<FaWhatsapp />}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!ready}
                            style={{ backgroundColor: ready ? WHATSAPP_GREEN : undefined }}
                        >
                            {t('Open in WhatsApp')}
                        </Button>
                    </Space>
                    {/* Telefonda QR anlamsız (zaten o cihazdasın); yalnız masaüstünde gösterilir. */}
                    {!isMobile && (
                        <Space direction="vertical" align="center" style={{ flex: '0 0 auto', margin: '0 auto' }}>
                            <QRCode
                                value={link}
                                size={220}
                                errorLevel="L"
                                status={ready ? 'active' : 'expired'}
                                statusRender={() => (
                                    <Typography.Text type="secondary" style={{ padding: 16, textAlign: 'center' }}>
                                        {t('Fill in the form first')}
                                    </Typography.Text>
                                )}
                            />
                            <Typography.Text type="secondary" style={{ fontSize: 12, maxWidth: 220, textAlign: 'center', display: 'block' }}>
                                {t('Scan with your phone to send via WhatsApp')}
                            </Typography.Text>
                        </Space>
                    )}
                </div>
            </Modal>
        </>
    );
}
